import { safeGetItem, safeRemoveItem, safeSetItem } from "./safeStorage";

export const SETUP_TOUR_STORAGE_KEY = "mib_setup_tour_v1";
export const SETUP_TOUR_EVENT = "setup-tour:status-changed";
export const SETUP_PAYMENTS_STATUS_EVENT = "setup-tour:payments-status";

export const SETUP_TOUR_STEPS = [
  {
    id: "profile",
    field: "org_profile_complete",
    title: "Profil einrichten",
    description:
      "Hier trägst du Firmenname, Kontakt und vollständige Rechnungsadresse ein und legst fest, wie die Oberfläche deine Kundschaft nennt. Prüfe die Rechnungs- und Steuerangaben vor der ersten Rechnung.",
  },
  {
    id: "studio",
    field: "location_added",
    title: "Standort einrichten",
    description:
      "Hier pflegst du deinen Standort mit Adresse, öffentlicher Seite und den wichtigsten Standort-Daten.",
  },
  {
    id: "services",
    field: "services_added",
    /* Als Funktion, weil die Beschriftung von der Betriebsart abhängt. Aufgelöst wird
       sie in SetupTourOverlay, wo das Vokabular verfügbar ist. */
    title: (vocab) => `${vocab.service.pl} anlegen`,
    description: (vocab) =>
      `Hier definierst du, was ${vocab.person.pl} buchen können: Dauer, Preis und Zuordnung zu Standort oder Mitarbeitenden.`,
  },
  {
    id: "booking",
    field: "booking_tested",
    title: "Buchungsseite testen",
    description: (vocab) =>
      `Hier testest du deine Buchungsseite so, wie ${vocab.person.pl} sie sehen.`,
  },
  {
    id: "payments",
    field: null,
    title: "Zahlungen prüfen",
    description:
      "Zum Schluss kannst du Online-Zahlungen und Anzahlungen aktivieren. Das ist optional und auch später möglich.",
  },
];

export const SETUP_TOUR_STEP_IDS = SETUP_TOUR_STEPS.map((step) => step.id);

/* Titel und Beschreibung dürfen Funktionen des Vokabulars sein. */
export function resolveSetupTourText(value, vocab) {
  return typeof value === "function" ? value(vocab) : value;
}

export function getSetupTourStep(stepId) {
  return SETUP_TOUR_STEPS.find((step) => step.id === stepId) || null;
}

export function getNextSetupTourStepId(stepId) {
  const index = SETUP_TOUR_STEP_IDS.indexOf(stepId);
  if (index === -1 || index >= SETUP_TOUR_STEP_IDS.length - 1) return null;
  return SETUP_TOUR_STEP_IDS[index + 1];
}

export function readSetupTourState() {
  try {
    const raw = safeGetItem(SETUP_TOUR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSetupTourState(nextState) {
  try {
    safeSetItem(SETUP_TOUR_STORAGE_KEY, JSON.stringify(nextState || {}));
  } catch {
    // ignore storage failures
  }
}

export function startSetupTour(stepId = "profile", extras = {}) {
  writeSetupTourState({
    active: true,
    currentStep: stepId,
    paymentsReviewed: false,
    updatedAt: Date.now(),
    ...extras,
  });
}

export function updateSetupTourState(patch) {
  const current = readSetupTourState() || {};
  writeSetupTourState({
    ...current,
    ...patch,
    updatedAt: Date.now(),
  });
}

export function clearSetupTourState() {
  try {
    safeRemoveItem(SETUP_TOUR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function dispatchSetupTourStatusChanged() {
  try {
    window.dispatchEvent(new Event(SETUP_TOUR_EVENT));
  } catch {
    // ignore
  }
}

export function getSetupTourResumeStep(status, state = readSetupTourState()) {
  const currentState = state || {};
  if (currentState.active && SETUP_TOUR_STEP_IDS.includes(currentState.currentStep)) {
    return currentState.currentStep;
  }

  for (const step of SETUP_TOUR_STEPS) {
    if (!step.field) continue;
    if (!status?.[step.field]) return step.id;
  }

  if (!status?.booking_tested) return "booking";
  if (!currentState.paymentsReviewed) return "payments";
  return null;
}

export function buildSetupTourTarget(stepId, context = {}) {
  const { locations = [] } = context;

  if (stepId === "profile") {
    return "/settings?tab=company&setup=1&setupStep=profile&focus=billing";
  }
  if (stepId === "studio") {
    const firstLocation = Array.isArray(locations) ? locations[0] : null;
    if (firstLocation?.id) {
      return `/locations/${firstLocation.id}?tab=details&setup=1&setupStep=studio&focus=details`;
    }
    return "/locations?setup=1&setupStep=studio";
  }
  if (stepId === "services") {
    return "/services?setup=1&setupStep=services";
  }
  if (stepId === "payments") {
    return "/settings?tab=advanced&setup=1&setupStep=payments&focus=payments";
  }
  if (stepId === "booking") {
    return "/dashboard?setup=1&setupStep=booking";
  }
  return "/dashboard";
}

export function buildStripeConnectReturnUrl(currentHref, { refresh = false } = {}) {
  const url = new URL(currentHref);
  url.searchParams.set("tab", "advanced");
  if (url.searchParams.get("setup") === "1" && url.searchParams.get("setupStep") === "payments") {
    url.searchParams.set("stripeConnect", refresh ? "refresh" : "returned");
  }
  return url.toString();
}
